/* eslint-disable no-console */

import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useRouterContext } from 'hooks';
import styled, { withTheme } from 'styled-components'
import { themeType } from 'types';
import { useEditorContext } from 'context/EditorContext';
import RichTextEditorMarkdown from 'containers/RichTextEditorMarkdown/RichTextEditorMarkdown'
import { Text as TextPortal } from '@uaveiro/ui';
import { withPortalProviders } from 'modules/utils';
import { useDebouncedCallback } from 'use-debounce';
import {
  Button, Spacer, Seperator, Text as TextEditor
} from 'components/atoms'
import { Tooltip } from 'components/molecules'
import { Authenticator } from 'containers'

const Text = (props) => withPortalProviders(TextPortal, props)

const ArticleEditor = ({
  initialProps, onChange, theme,
}) => {
  const {
    match: {
      params: { lang },
    },
  } = useRouterContext();
  const [isRaw, setRaw] = useState(false)

  const initialMarkdown = useMemo(() => {
    if (!initialProps) return ''

    const { data } = initialProps
    if (data && data[lang] && data[lang].content) return data[lang].content
    if (data && data.content) return data.content
    return ''
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [lang])

  const activeColor = useMemo(() => {
    if (!initialProps) return theme.colors.green
    if (initialProps.activeColor) return initialProps.activeColor
    return theme.colors.green
  }, [initialProps, theme.colors.green])

  // debounce value
  const handleChangeMarkdown = useDebouncedCallback(
    (newMarkdown) => {
      const newProps = { ...initialProps }
      if (newProps.data[lang] && newProps.data[lang].content) {
        onChange({
          ...newProps,
          data: {
            ...newProps.data,
            [lang]: {
              ...newProps.data[lang],
              content: newMarkdown
            }
          }
        })
      }
      if (newProps.data && !newProps.data[lang]) {
        onChange({
          ...newProps,
          data: {
            ...newProps.data,
            content: newMarkdown
          }
        })
      }
    },
    500
  );

  const [{ screen }] = useEditorContext();

  // title edition
  const initialTitle = useMemo(() => {
    if (!initialProps) return ''

    const { data } = initialProps
    if (data && data[lang] && data[lang].title) return data[lang].title
    if (data && data.title) return data.title
    return null
  },
  [initialProps, lang])

  return (
    <Section>
      <div className="container">
        <div className="row no-gutters">
          <div className={screen === 'desktop' ? 'col-xl-6 offset-xl-3 col-lg-8 offset-lg-2 col-12 offset-0' : 'col-12'}>
            <Top hasTitle={initialTitle}>
              {initialTitle && (
                <Text
                  as="h1"
                  fontWeight="500"
                  text={initialTitle}
                  size="xLarge"
                  firstLetter="capitalize"
                />
              )}
            </Top>
            <RichTextEditorMarkdown
              initialMarkdown={initialMarkdown}
              onEditorStateChange={handleChangeMarkdown}
              options={{
                activeColor
              }}
              isRaw={isRaw}
            />
            {!isRaw && (
              <Authenticator requires="admin">
                <Spacer top="l" bottom="l">
                  <Seperator />
                </Spacer>
                <TextEditor modifiers={['xs', 'light']}>
              Ferramentas de administrador:
                </TextEditor>
                <Spacer bottom="l" />
                <Tooltip tooltip="editar markdown" placement="top">
                  <Button modifiers={['smaller', 'dark']} onClick={() => setRaw(!isRaw)} icon="fal fa-code">
                Editar markdown
                  </Button>
                </Tooltip>
              </Authenticator>
            )}
          </div>
        </div>
      </div>
    </Section>
  );
};

ArticleEditor.propTypes = {
  onChange: PropTypes.func,
  initialProps: PropTypes.shape({
    activeColor: PropTypes.string,
    data: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        pt: PropTypes.shape({
          content: PropTypes.string,
        }),
        en: PropTypes.shape({
          content: PropTypes.string,
        })
      })])
  }).isRequired,
  theme: themeType.isRequired,
};

ArticleEditor.defaultProps = {
  onChange: () => null,
};

const Section = styled.div`
  min-height: auto;
  padding-top: 4.375rem;
  padding-bottom: 4.375rem;
  background-color: #fff;
  position: relative;
`

const Top = styled.div`
  display: flex;
  justify-content: ${props => (props.hasTitle ? 'space-between' : 'flex-end')};
  margin-bottom: ${props => props.theme.spacing.xs3};
  align-items: center;

`

export default withTheme(ArticleEditor);
